import { Injectable } from "@angular/core"
import { ApiService } from "app/shared/services/api.service"
import { UPLOAD_BATCH_MAX_BYTES, UPLOAD_BATCH_MAX_FILES } from "@shared/constants/file-size.constants"
import { toFileList } from "@shared/utils/to-file-list"
import { concat, Observable, toArray } from "rxjs"
import { PaymentMethodCleanupRequest, PaymentMethodCleanupResponse } from "./models/payment-method-cleanup"
import { BetaFeatures } from "./models/BetaFeatures"
import { MediaFile } from "./models/media-library"

const API = {
  V1: "utility/API/V1/",
  Utility_PaymentMethodCleanup: "utility/PaymentMethodCleanup",
  Media_Library: "utility/API/V1/MediaLibrary/Hotel",
}

@Injectable({
  providedIn: "root",
})
export class UtilityApiService {
  constructor(private apiService: ApiService) {}

  public MediaLibrary = {
    /**Upload one or multiple new files */
    uploadFile: (hotelId: string, parentId: string, files: FileList, pathsMap: Map<string, string>, fileId: string = ""): Observable<any> => {
      var fileArray = Array.from(files)
      const newFileID = fileId === "" ? "" : `&id=${fileId}`
      var formData: FormData = new FormData()
      fileArray.forEach((file, index) => {
        if (file.webkitRelativePath === "") {
          pathsMap !== null ? formData.append("formFiles", file, pathsMap.get(file.name)) : formData.append("formFiles", file, file.name)
        } else {
          formData.append("formFiles", file, file.webkitRelativePath)
        }
      })
      return this.apiService.postFormData<any>(`${API.Media_Library}/${hotelId}/File/Upload?parentId=${parentId}${newFileID}`, formData)
    },
    /**Upload files as sequential batches, keeping each request body under the server limit */
    uploadFileBatched: (hotelId: string, parentId: string, files: FileList, pathsMap: Map<string, string>): Observable<any> => {
      const batches = this.splitIntoBatches(Array.from(files))

      if (batches.length <= 1) {
        return this.MediaLibrary.uploadFile(hotelId, parentId, files, pathsMap)
      }

      const requests = batches.map((batch) => this.MediaLibrary.uploadFile(hotelId, parentId, toFileList(batch), pathsMap))
      return concat(...requests).pipe(toArray())
    },
    /**Replace file with another file keeping everything else */
    replaceFile: (hotelId: string, parentId: string, files: FileList, id: string): Observable<any> => {
      var fileArray = Array.from(files)
      var formData: FormData = new FormData()
      fileArray.forEach((file, index) => {
        formData.append("formFiles", file, file.name)
      })
      return this.apiService.postFormData<any>(`${API.Media_Library}/${hotelId}/File/Upload?parentId=${parentId}&id=${id}`, formData)
    },
    /**Get specific file information and url link */
    getFile: (hotelId: string, fileId: string): Observable<MediaFile> => this.apiService.get<MediaFile>(`${API.Media_Library}/${hotelId}/File/${fileId}`),
    /**Get specific folder content */
    getFolder: (hotelId: string, parentId: string): Observable<MediaFile[]> => this.apiService.get<MediaFile[]>(`${API.Media_Library}/${hotelId}/Folder/${parentId}/Get`),
    /**Create new folder */
    createFolder: (hotelId: string, parentId: string, folderName: string): Observable<MediaFile> => this.apiService.post<MediaFile>(`${API.Media_Library}/${hotelId}/Folder/${parentId}/Create?folderName=${folderName}`),
    /**Rename file */
    renameFile: (hotelId: string, media: MediaFile): Observable<any> => this.apiService.patch<any>(`${API.Media_Library}/${hotelId}/Media/Update`, media),
    /**Move file to new destination */
    moveFile: (hotelId: string, targetParentId: string, list: MediaFile[]): Observable<any> => this.apiService.put<any>(`${API.Media_Library}/${hotelId}/Media/Move/${targetParentId}`, list),
    /**Delete file */
    deleteFile: (hotelId: string, media: MediaFile[]): Observable<any> => this.apiService.delete(`${API.Media_Library}/${hotelId}/Media/Delete`, media),
    /**Delete permanetly */
    hardDelete: (hotelId: string, mediaList: MediaFile[]): Observable<any> => this.apiService.delete(`${API.Media_Library}/${hotelId}/Media/HardDelete`, mediaList),
    /**Undelete file */
    unDeleteFile: (hotelId: string, targetLocationId: string, media: MediaFile[]): Observable<any> => this.apiService.patch<any>(`${API.Media_Library}/${hotelId}/Media/Undelete?targetLocationID=${targetLocationId}`, media),
    /**Get breacrumb for media */
    getBreadCrumb: (hotelId: string, mediaId: string): Observable<MediaFile[]> => this.apiService.get<MediaFile[]>(`${API.Media_Library}/${hotelId}/Media/${mediaId}/BreadCrumbs`),
    /**Get all media for hotel */
    getAllMedia: (hotelId: string, notDeleted: boolean): Observable<MediaFile[]> => this.apiService.get<MediaFile[]>(`${API.Media_Library}/${hotelId}/Media/GetAll?deleted=${notDeleted}`),
  }

  public PaymentMethodCleanup = {
    /**Get current configuration of PaymentMethodCleanup */
    getConfiguration: (hotelid: string): Observable<PaymentMethodCleanupResponse> => this.apiService.get<PaymentMethodCleanupResponse>(`${API.Utility_PaymentMethodCleanup}/GetPaymentMethodCleanupConfiguration/${hotelid}`),

    /**Post a new configuration of PaymentMethodCleanup */
    postConfiguration: (paymentMethodDelay: PaymentMethodCleanupRequest): Observable<PaymentMethodCleanupResponse> => this.apiService.post<PaymentMethodCleanupResponse>(`${API.Utility_PaymentMethodCleanup}/CreatePaymentMethodCleanupConfiguration`, paymentMethodDelay),

    /**Update current configuration of PaymentMethodCleanup */
    putConfiguration: (paymentMethodDelay: PaymentMethodCleanupRequest): Observable<PaymentMethodCleanupResponse> => this.apiService.put<PaymentMethodCleanupResponse>(`${API.Utility_PaymentMethodCleanup}/UpdatePaymentMethodCleanupConfiguration`, paymentMethodDelay),
  }
  public V1BetaFeatures = {
    getFeatures: (hotelId: string): Observable<BetaFeatures> => this.apiService.get<BetaFeatures>(`${API.V1}BetaFeature?hotelId=${hotelId}`),
  }

  private splitIntoBatches(files: File[]): File[][] {
    const batches: File[][] = []
    let current: File[] = []
    let currentBytes = 0

    files.forEach((file) => {
      const exceedsBytes = currentBytes + file.size > UPLOAD_BATCH_MAX_BYTES
      const exceedsCount = current.length >= UPLOAD_BATCH_MAX_FILES

      if (current.length > 0 && (exceedsBytes || exceedsCount)) {
        batches.push(current)
        current = []
        currentBytes = 0
      }

      current.push(file)
      currentBytes += file.size
    })

    if (current.length > 0) batches.push(current)
    return batches
  }
}
